import { Link, usePage } from '@inertiajs/react';
import { useTranslation, Trans } from 'react-i18next';
import { Flex, Progress, Text } from '@radix-ui/themes';
import useRouter from '@/hooks/useRouter';

import { BuildingsIcon } from '@/components/icons';
import Button from '@/components/Button';
import InfoCallout from '@/components/InfoCallout';

export default function FirmNavbar({ firm }) {
  const { t } = useTranslation();
  const { router, route } = useRouter();
  const { component: currentPage } = usePage();
  const isDashboard = currentPage === 'firms/DashboardPage';
  const totalSteps = Object.keys(firm.setup_steps).length;
  const completedSteps = Object.values(firm.setup_steps).filter(Boolean).length;
  const setupProgress = Math.round((completedSteps / totalSteps) * 100);
  const isFirmPending = firm.state === 'pending';
  const isFirmApproved = firm.state === 'approved';
  const isFirmLive = firm.live;

  return (
    <Flex className="text-neutral-800 font-semibold text-[15px] ">
      <Flex direction="column" gap="1" className="w-fit">
        <Link
          href={route(`/firms/${firm.slug}`)}
          className="outline-(--accent-8)"
        >
          <Flex align="center" gap="2">
            <BuildingsIcon size={22} className="inline-block shrink-0" />
            <Text>{firm.name}</Text>
          </Flex>
        </Link>

        {isFirmPending && (
          <Flex
            align="center"
            gap="3"
            className={
              isDashboard ? 'hidden' : 'hidden lg:flex animate-slide-up'
            }
          >
            <Text color="gray" size="1" weight="regular">
              {t('firm.account_setup.heading')}:
            </Text>

            <Flex className="w-18">
              <Progress
                color="teal"
                value={setupProgress}
                className="w-full"
                data-view-transition-name="firm-setup-progress"
              />
            </Flex>

            <Text color="gray" size="1" weight="regular">
              {t('firm.account_setup.steps_completed', {
                completedSteps,
                totalSteps,
              })}
            </Text>

            <Button
              size="1"
              variant="outline"
              radius="full"
              color="teal"
              onClick={() => router.visit(`/firms/${firm.slug}`)}
            >
              {t('firm.account_setup.next_steps')}
            </Button>
          </Flex>
        )}

        {isFirmApproved && !isFirmLive && (
          <Flex
            align="center"
            gap="3"
            className={
              isDashboard ? 'hidden' : 'hidden lg:flex animate-slide-up'
            }
          >
            <InfoCallout className="py-0.5 px-2 font-normal">
              <Trans i18nKey="firm.account_status.approved" />
            </InfoCallout>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
